import type { BlockT, InputT } from "@patient-watch/types";
import type { SumEqualsRule } from "@patient-watch/forms/pageValidation";
import { type CarouselImage, parseCarouselImages } from "@/features/blocks/utils/parseCarouselImages";

export interface ImageCountRow {
    input: InputT;
    image: CarouselImage;
}

export interface ImageCountsLayout {
    /** The carousel block. The page draws it inside the group, so it is left out of the plain block list. */
    carouselBlockId: string;
    rows: ImageCountRow[];
}

/**
 * Pairs the pictures of the page's image carousel with the inputs of a `sum_equals` rule that asks for the
 * `image_counts` layout: picture `i` goes with `rule.input_ids[i]`. Returns null when the page cannot be
 * laid out that way (no carousel, a different number of pictures and inputs, or an input missing), so the
 * caller falls back to the plain page.
 */
export function pairImageCounts(
    rule: SumEqualsRule | null,
    inputs: readonly InputT[],
    blocks: readonly BlockT[]
): ImageCountsLayout | null {
    if (!rule) return null;
    const carousel = blocks.find((block) => (block.block_type as string) === "image-carousel");
    if (!carousel) return null;
    const images = parseCarouselImages(carousel.content);
    if (images.length === 0 || images.length !== rule.input_ids.length) return null;

    const rows: ImageCountRow[] = [];
    for (const [index, inputId] of rule.input_ids.entries()) {
        const input = inputs.find((candidate) => candidate.input_id === inputId);
        if (!input) return null;
        rows.push({ input, image: images[index]! });
    }
    return { carouselBlockId: carousel.block_id, rows };
}

/**
 * The count after pressing plus or minus. A blank count is not 0: plus from blank gives 1 and minus gives 0,
 * so the first press always records an answer. The result stays within min and max.
 */
export function stepCount(current: number | null, direction: 1 | -1, min: number, max: number): number {
    const next = current === null ? (direction === 1 ? min + 1 : min) : current + direction;
    return Math.min(Math.max(next, min), max);
}
